import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import PageMeta from './PageMeta';
import ForAudienceNavStrip from './ForAudienceNavStrip';
import { PRO_LANDING_INTEREST_EMAIL_KEY, OWNER_PRO_INVITE_TOKEN_KEY } from '../utils/professionalOnboardingConstants';

/** Optional notes from hero; stored for a future onboarding step (sessionStorage). */
const PRO_LANDING_SIGNUP_NOTES_KEY = 'proLandingSignupNotes';

const FLOW_STEPS = [
  {
    id: 'pool',
    label: '1. Open jobs',
    title: 'See real maintenance requests',
    copy: 'Browse requests that match your trade categories and service area—no bidding wars on unrelated work.',
  },
  {
    id: 'propose',
    label: '2. Claim & propose',
    title: 'Raise your hand with clear times',
    copy: 'Claim a job or propose up to three visit windows. Tenants and owners are notified so scheduling stays in one thread.',
  },
  {
    id: 'scheduled',
    label: '3. Scheduled',
    title: 'Full context on the day of the visit',
    copy: 'Once someone accepts your slot, you get unit, address, issue details, and owner plus tenant contacts when the record has them.',
  },
  {
    id: 'estimate',
    label: '4. Estimates & pay',
    title: 'Quote once, get paid smoothly',
    copy: 'Send clear estimates with photos from the same thread as the job. When the owner accepts, payment runs through the platform so you spend less time chasing invoices and more time on the next ticket.',
  },
  {
    id: 'reputation',
    label: '5. Done & rated',
    title: 'Ratings from both sides',
    copy: 'Complete the job; tenants and owners can rate you. Strong history helps you stand out when multiple pros propose on the same request.',
  },
];

const FLOW_AUTO_ADVANCE_MS = 8000;

/** Shown on-page; confirmed at checkout. */
const PRO_SCREENING_DEPOSIT_DISPLAY = '$20';

const FAQ_ITEMS = [
  {
    q: 'Is signup open to everyone today?',
    a: 'We are building a trusted network. Create an account and complete our guided onboarding — including trade selection, qualifications, and a refundable deposit. That helps protect property owners and residents while giving you fast access to real jobs.',
  },
  {
    q: 'What does onboarding include?',
    a: `We verify your business details, trade credentials (license or registration where required), and insurance when it applies—plus identity confirmation and a background screen through a licensed screening partner where the law allows it for your role and region. Screening is funded with a refundable deposit (${PRO_SCREENING_DEPOSIT_DISPLAY}—see the next questions). The rest is self-serve uploads and automated checks; we only reach out if something needs a closer look.`,
  },
  {
    q: 'Do I pay for the background check?',
    a: `Before our partner runs identity and background screening, we collect a refundable deposit of ${PRO_SCREENING_DEPOSIT_DISPLAY}. If Tenant Dispatch does not approve you, the deposit is refunded — you are not buying a permanent fee. If you are approved, the deposit is refunded after your first completed paid job through Tenant Dispatch.`,
  },
  {
    q: 'Is the deposit ever non-refundable?',
    a: 'Almost never for ordinary outcomes. If we decline your application—including when screening results do not meet our published standards for the network—you still get the deposit back. Review those standards before you pay so you can decide whether applying is right for you. We may withhold a refund only in narrow cases spelled out in the terms at payment, such as verified fraud or deliberate false information—not simply because a report came back with records you disagree with.',
  },
  {
    q: 'Do I pay for leads?',
    a: 'No. Applying is free, and so is browsing open requests in your categories and service area—including proposing visit times. If you complete paid work through Tenant Dispatch, we share how the platform participates in that success before you go live; there is nothing to buy just to see or pitch on jobs.',
  },
  {
    q: 'How do I get paid?',
    a: 'When an owner pays for your work through Tenant Dispatch, we route funds to the payout method you set up during onboarding (for example linked bank details). If a job is handled outside the product, payment stays between you and the owner—we walk through both paths when you are approved.',
  },
  {
    q: 'What if I already signed up on the main site?',
    a: 'If you created an account before we tightened onboarding, email hello@tenantdispatch.com from your business address and we will help line your profile up with the current process.',
  },
];

function ProLandingFlowMock({ stepId }) {
  const rows = {
    pool: (
      <>
        <div className="pro-landing-flow-row">
          <span className="pro-landing-flow-tag">Plumbing</span>
          <span><strong>Leak under kitchen sink</strong> — 2.4 mi · Urgent</span>
        </div>
        <div className="pro-landing-flow-row">
          <span className="pro-landing-flow-tag pro-landing-flow-tag--blue">HVAC</span>
          <span><strong>No heat — bedroom zone</strong> — 5.1 mi</span>
        </div>
        <div className="pro-landing-flow-row">
          <span className="pro-landing-flow-tag pro-landing-flow-tag--amber">Electrical</span>
          <span><strong>Outlet sparking</strong> — 1.1 mi · Urgent</span>
        </div>
      </>
    ),
    propose: (
      <>
        <div className="pro-landing-flow-row">
          <span className="pro-landing-flow-tag">Claimed</span>
          <span>You · Proposed Tue 9:00a, Wed 2:00p, Thu 10:30a</span>
        </div>
        <div className="pro-landing-flow-row">
          <span className="pro-landing-flow-tag pro-landing-flow-tag--violet">Waiting</span>
          <span>Owner or client picks a time · Notifications sent</span>
        </div>
      </>
    ),
    scheduled: (
      <>
        <div className="pro-landing-flow-row">
          <span className="pro-landing-flow-tag">Visit</span>
          <span><strong>Wed 2:00 PM</strong> · Unit 4B · Full address on card</span>
        </div>
        <div className="pro-landing-flow-row">
          <span className="pro-landing-flow-tag pro-landing-flow-tag--blue">Contacts</span>
          <span>Property owner + tenant when on file</span>
        </div>
      </>
    ),
    estimate: (
      <>
        <div className="pro-landing-flow-row">
          <span className="pro-landing-flow-tag">Estimate</span>
          <span><strong>$285</strong> labor + parts · 2 photos attached</span>
        </div>
        <div className="pro-landing-flow-row">
          <span className="pro-landing-flow-tag pro-landing-flow-tag--amber">Status</span>
          <span>Awaiting owner acceptance · Locked after pay</span>
        </div>
      </>
    ),
    reputation: (
      <>
        <div className="pro-landing-flow-row">
          <span className="pro-landing-flow-tag">Completed</span>
          <span>Job closed · Receipt and notes on file</span>
        </div>
        <div className="pro-landing-flow-row">
          <span className="pro-landing-flow-tag pro-landing-flow-tag--violet">Ratings</span>
          <span>Tenant ★★★★★ · Owner ★★★★★</span>
        </div>
      </>
    ),
  };

  return (
    <div className="pro-landing-flow-mock" aria-hidden>
      <div className="pro-landing-flow-mock-header">
        <span className="pro-landing-flow-mock-title">Pro Dispatch preview</span>
      </div>
      {rows[stepId] || rows.pool}
    </div>
  );
}

const ProfessionalLandingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [navSolid, setNavSolid] = useState(false);
  const [showStickyCta, setShowStickyCta] = useState(false);
  const [flowStep, setFlowStep] = useState(0);
  const [faqOpen, setFaqOpen] = useState(null);
  const [ctaError, setCtaError] = useState('');

  const handleGetStarted = useCallback(() => {
    const trimmed = email.trim();
    if (!trimmed) {
      setCtaError('Please enter your business email.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setCtaError('Please enter a valid email address.');
      return;
    }
    setCtaError('');
    try {
      const n = notes.trim().slice(0, 500);
      if (n) {
        sessionStorage.setItem(PRO_LANDING_SIGNUP_NOTES_KEY, n);
      } else {
        sessionStorage.removeItem(PRO_LANDING_SIGNUP_NOTES_KEY);
      }
    } catch (e) {
      console.warn('sessionStorage pro landing notes:', e);
    }
    try {
      sessionStorage.setItem(PRO_LANDING_INTEREST_EMAIL_KEY, trimmed);
    } catch (e) {
      console.warn('sessionStorage pro landing email:', e);
    }
    navigate(`/for-professionals/join?email=${encodeURIComponent(trimmed)}`);
  }, [email, notes, navigate]);

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (ctaError) setCtaError('');
  };

  const handleNotesChange = (e) => {
    setNotes(e.target.value.slice(0, 500));
    if (ctaError) setCtaError('');
  };

  const scrollToId = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const token = new URLSearchParams(location.search).get('proInvite')?.trim();
    if (!token) return;
    try {
      sessionStorage.setItem(OWNER_PRO_INVITE_TOKEN_KEY, token);
    } catch (e) {
      console.warn('sessionStorage owner pro invite token:', e);
    }
  }, [location.search]);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return undefined;
    }
    const id = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      setFlowStep((s) => (s + 1) % FLOW_STEPS.length);
    }, FLOW_AUTO_ADVANCE_MS);
    return () => clearInterval(id);
  }, [flowStep]);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setNavSolid(y > 56);
      setShowStickyCta(y > 420);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const step = FLOW_STEPS[flowStep];

  return (
    <div className="realtor-landing-page professional-landing-theme">
      <PageMeta
        title="Tenant Dispatch for Service Professionals — Maintenance Jobs and Fair Scheduling"
        description="Join a vetted network of tradespeople. See maintenance requests that match your skills, propose visit times, send estimates, and get paid—with owner and tenant context in one place."
        keywords="handyman software, maintenance contractor app, property maintenance jobs, HVAC plumber electrician leads, service professional scheduling"
      />

      <header className={`realtor-nav ${navSolid ? 'realtor-nav--solid' : 'realtor-nav--hero'}`}>
        <div className="realtor-nav-inner">
          <div className="realtor-nav-start">
            <Link to="/" className="realtor-nav-brand" aria-label="Tenant Dispatch — back to home">
              <img src={`${process.env.PUBLIC_URL}/tenant-dispatch-icon-192.png`} alt="" className="realtor-nav-logo" width={36} height={36} />
              <span className="realtor-nav-brand-text">For Professionals</span>
            </Link>
            <ForAudienceNavStrip variant="realtor" activeAudience="professionals" />
          </div>
          <nav className="realtor-nav-links" aria-label="Page sections">
            <button type="button" onClick={() => scrollToId('pro-flow')}>Flow</button>
            <button type="button" onClick={() => scrollToId('pro-bento')}>Capabilities</button>
            <button type="button" onClick={() => scrollToId('pro-vetting')}>Onboarding</button>
            <button type="button" onClick={() => scrollToId('pro-compare')}>Compare</button>
            <button type="button" onClick={() => scrollToId('pro-faq')}>FAQ</button>
          </nav>
          <button type="button" className="realtor-nav-cta" onClick={() => scrollToId('pro-cta')}>
            Get started
          </button>
        </div>
      </header>

      <main className="realtor-landing-main">
        <section id="pro-hero" className="realtor-hero">
          <div className="realtor-hero-content">
            <div className="realtor-badge">🛠️ Residential maintenance, not random gig spam</div>
            <h1 className="realtor-hero-title">
              Win work with{' '}
              <span className="realtor-hero-accent">clear context and scheduling</span>
            </h1>
            <p className="realtor-hero-subtitle">
              Tenant Dispatch connects you to real repair requests from property owners and residents. <strong>Seeing matching jobs and pitching visit times costs nothing</strong>—no pay-per-lead to open the board in your trade and area. From there, share estimates, get paid, and build a reputation while we keep onboarding selective so the marketplace stays trustworthy.
            </p>
            <div className="realtor-hero-cta">
              <div className="realtor-email-input">
                <input
                  type="email"
                  placeholder="Business email"
                  value={email}
                  onChange={handleEmailChange}
                  onKeyDown={(e) => e.key === 'Enter' && handleGetStarted()}
                  aria-label="Business email"
                />
                <button
                  type="button"
                  onClick={handleGetStarted}
                  className="realtor-cta-primary"
                >
                  Get started
                  <span className="cta-arrow" aria-hidden>→</span>
                </button>
              </div>
              <div className="pro-landing-notes-wrap">
                <label htmlFor="pro-landing-notes">Trade &amp; service area (optional)</label>
                <textarea
                  id="pro-landing-notes"
                  className="pro-landing-notes"
                  placeholder="e.g. Licensed plumber, Boston metro, EPA lead-safe certified"
                  value={notes}
                  onChange={handleNotesChange}
                  rows={3}
                  maxLength={500}
                />
                <p className="pro-landing-notes-hint">Helps us route your signup. Max 500 characters.</p>
              </div>
              <div className="realtor-hero-actions-row">
                <button type="button" className="realtor-hero-secondary" onClick={() => scrollToId('pro-flow')}>
                  See the workflow
                </button>
              </div>
              <p className="realtor-cta-note">{`Vetted onboarding • Free to browse leads • ${PRO_SCREENING_DEPOSIT_DISPLAY} refundable deposit • No card until onboarding`}</p>
              {ctaError && (
                <p role="alert" className="realtor-access-feedback realtor-access-feedback--hero realtor-access-feedback--error">
                  {ctaError}
                </p>
              )}
            </div>
          </div>
          <div className="realtor-hero-visual">
            <div className="realtor-dashboard-preview" aria-hidden>
              <div className="preview-header">
                <div className="preview-dots">
                  <span /><span /><span />
                </div>
                <span className="preview-title">Pro workflow</span>
              </div>
              <div className="realtor-preview-content">
                <div className="preview-card green">
                  <span className="card-icon">📋</span>
                  <span>Available → match trade &amp; radius</span>
                </div>
                <div className="preview-card blue">
                  <span className="card-icon">📅</span>
                  <span>Propose times · owner &amp; tenant notified</span>
                </div>
                <div className="preview-card yellow">
                  <span className="card-icon">🧾</span>
                  <span>Estimates sent in-app · paid without the runaround</span>
                </div>
                <div className="preview-card purple">
                  <span className="card-icon">⭐</span>
                  <span>Ratings from tenant &amp; owner</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="realtor-trust-strip" role="presentation">
          <span>Free to browse leads</span>
          <span className="realtor-trust-dot" aria-hidden>·</span>
          <span>Category-matched jobs</span>
          <span className="realtor-trust-dot" aria-hidden>·</span>
          <span>Multi-party scheduling</span>
          <span className="realtor-trust-dot" aria-hidden>·</span>
          <span>Estimates &amp; payments</span>
          <span className="realtor-trust-dot" aria-hidden>·</span>
          <span>Vetted network</span>
        </div>

        <section className="realtor-paths" aria-label="Choose what to explore">
          <div className="realtor-paths-inner">
            <button type="button" className="realtor-path-card" onClick={() => scrollToId('pro-flow')}>
              <span className="realtor-path-card-kicker">I want the tour</span>
              <span className="realtor-path-card-title">Step through the flow</span>
            </button>
            <button type="button" className="realtor-path-card" onClick={() => scrollToId('pro-bento')}>
              <span className="realtor-path-card-kicker">I am comparing platforms</span>
              <span className="realtor-path-card-title">See capabilities</span>
            </button>
            <button type="button" className="realtor-path-card" onClick={() => scrollToId('pro-vetting')}>
              <span className="realtor-path-card-kicker">I care about trust</span>
              <span className="realtor-path-card-title">How onboarding works</span>
            </button>
          </div>
        </section>

        <section id="pro-flow" className="realtor-flow-section">
          <div className="section-header">
            <span className="section-badge">Interactive overview</span>
            <h2 className="section-title">How Tenant Dispatch fits your week</h2>
            <p className="section-subtitle">
              Same ideas as the live product: discover work, coordinate with residents and owners, finish strong. The preview advances automatically, or pick a step.
            </p>
          </div>
          <div className="realtor-flow-layout">
            <div className="realtor-flow-steps" aria-label="Flow steps">
              {FLOW_STEPS.map((s, i) => (
                <button
                  key={s.id}
                  type="button"
                  aria-pressed={flowStep === i}
                  className={`realtor-flow-step-btn ${flowStep === i ? 'is-active' : ''}`}
                  onClick={() => setFlowStep(i)}
                >
                  <span className="realtor-flow-step-label">{s.label}</span>
                  <span className="realtor-flow-step-title">{s.title}</span>
                </button>
              ))}
            </div>
            <div className="realtor-flow-panel">
              <div className="realtor-flow-panel-chrome">
                <div className="realtor-flow-panel-dots"><span /><span /><span /></div>
                <span className="realtor-flow-panel-title">Tenant Dispatch</span>
              </div>
              <div className="realtor-flow-panel-body">
                <div
                  key={step.id}
                  className="realtor-flow-step-transition"
                  aria-live="polite"
                >
                  <h3 className="realtor-flow-panel-heading">{step.title}</h3>
                  <p className="realtor-flow-panel-copy">{step.copy}</p>
                  <div className={`realtor-flow-visual realtor-flow-visual--${step.id}`}>
                    <ProLandingFlowMock stepId={step.id} />
                  </div>
                  <div className="realtor-flow-nav">
                    <button
                      type="button"
                      className="realtor-flow-back"
                      disabled={flowStep === 0}
                      onClick={() => setFlowStep((x) => Math.max(0, x - 1))}
                    >
                      Back
                    </button>
                    <button
                      type="button"
                      className="realtor-flow-next"
                      disabled={flowStep === FLOW_STEPS.length - 1}
                      onClick={() => setFlowStep((x) => Math.min(FLOW_STEPS.length - 1, x + 1))}
                    >
                      Next step
                    </button>
                  </div>
                  <p className="realtor-flow-auto-hint">
                    Auto-advances every {FLOW_AUTO_ADVANCE_MS / 1000}s while this tab is visible, then loops. Respects reduced motion. Use the step buttons anytime.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="pro-bento" className="realtor-bento-section">
          <div className="section-header">
            <span className="section-badge">Capabilities</span>
            <h2 className="section-title">Built for field work, not lead auctions</h2>
            <p className="section-subtitle">Every feature ties back to one goal: less phone tag, fewer surprises on site, faster payment when the job is done right.</p>
          </div>
          <div className="realtor-bento">
            <article className="realtor-bento-tile realtor-bento-tile--hero">
              <div className="realtor-bento-icon">🎯</div>
              <h3>Jobs that fit your trade</h3>
              <p>Requests are tagged by category and location. You see work you can actually perform—without wading through unrelated leads.</p>
            </article>
            <article className="realtor-bento-tile">
              <div className="realtor-bento-icon">💬</div>
              <h3>Everyone in the loop</h3>
              <p>When both owner and tenant exist on a ticket, notifications and scheduling can reach both—so you are not stuck in the middle of a broken thread.</p>
            </article>
            <article className="realtor-bento-tile">
              <div className="realtor-bento-icon">🧾</div>
              <h3>Estimates with a paper trail</h3>
              <p>Submit scope and photos; accepted estimates lock so expectations stay clear through payment.</p>
            </article>
            <article className="realtor-bento-tile realtor-bento-tile--wide">
              <div className="realtor-bento-icon">🔐</div>
              <h3>Trust by design</h3>
              <p>We onboard professionals deliberately. Owners and tenants should feel confident inviting someone onto their property—that starts with who is allowed on the platform.</p>
            </article>
          </div>
        </section>

        <section id="pro-vetting" className="realtor-early-section">
          <div className="section-header">
            <span className="section-badge">Onboarding</span>
            <h2 className="section-title">Vetted, without the runaround</h2>
            <p className="section-subtitle">
              {`We review every application so owners and tenants know who is on site. Expect checklists and uploads—not a screening call for everyone. Screening uses a refundable ${PRO_SCREENING_DEPOSIT_DISPLAY} deposit; refund timing and eligibility are in the steps below and in your onboarding flow.`}
            </p>
          </div>
          <div className="realtor-compare-table-wrap" style={{ maxWidth: 800 }}>
            <table className="realtor-compare-table">
              <thead>
                <tr>
                  <th scope="col">Step</th>
                  <th scope="col">What to expect</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th scope="row">1. Apply</th>
                  <td>
                    You submit your business email and optional notes about your trade and area. We confirm we received you and email back with timing and next actions—usually within a few business days.
                  </td>
                </tr>
                <tr>
                  <th scope="row">2. Credentials</th>
                  <td>
                    Depending on your trade and region, we may ask for things like license or registration details, proof of insurance, or a tax form for payouts. We tell you exactly what to send; you upload or reply by email—no guessing.
                  </td>
                </tr>
                <tr>
                  <th scope="row">3. Identity &amp; screening</th>
                  <td>
                    {`You place a refundable deposit (${PRO_SCREENING_DEPOSIT_DISPLAY}) so our partner can run identity verification and, where allowed for your trade and location, a background check. The deposit comes back if you are not approved; if you are approved, it is refunded after your first completed paid job through Tenant Dispatch. No reference calls or mandatory video interviews.`}
                  </td>
                </tr>
                <tr>
                  <th scope="row">4. Automated review</th>
                  <td>
                    Your application, credentials, and screening results are reviewed against our standards. Most approvals are routine; edge cases (conflicting info, expired documents, or policy exceptions) get a targeted follow-up from our team instead of a blanket call for everyone.
                  </td>
                </tr>
                <tr>
                  <th scope="row">5. Welcome aboard</th>
                  <td>
                    You get access to finish your profile—categories, service area, the basics—and start viewing requests that match. We stay available if your first jobs need a hand.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section id="pro-compare" className="realtor-compare-section">
          <div className="section-header">
            <span className="section-badge">Reality check</span>
            <h2 className="section-title">Vague leads · jobs with the full picture</h2>
            <p className="section-subtitle">
              We are not your truck and tools—we are the thread between you, the owner, and the tenant so residential repairs move from request to paid work without losing context.
            </p>
          </div>
          <div className="realtor-compare-table-wrap">
            <table className="realtor-compare-table">
              <thead>
                <tr>
                  <th scope="col">Topic</th>
                  <th scope="col">Most lead apps</th>
                  <th scope="col">This network</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th scope="row">Context</th>
                  <td>Short blurbs; you chase basics by text</td>
                  <td>Issue, unit, owner and tenant when the ticket has them</td>
                </tr>
                <tr>
                  <th scope="row">Scheduling</th>
                  <td>You and the customer coordinate in a silo</td>
                  <td>You propose windows; owner and tenant accept in their dashboards</td>
                </tr>
                <tr>
                  <th scope="row">Seeing jobs</th>
                  <td>Pay-per-lead, subscriptions, or bids to unlock listings</td>
                  <td>Browse and propose for free—no charge just to see open work</td>
                </tr>
                <tr>
                  <th scope="row">Getting paid</th>
                  <td>Often off-platform; inconsistent paper trail</td>
                  <td>In-app estimates and checkout when owners pay here; we only take a cut on completed paid jobs—terms at onboarding</td>
                </tr>
                <tr>
                  <th scope="row">Who gets in</th>
                  <td>Open signup; crowded listings</td>
                  <td>Vetted pros; fewer mismatches for owners and tenants</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section className="realtor-security-section">
          <div className="realtor-security-inner">
            <h2 className="realtor-security-title">Trust on the job</h2>
            <p className="realtor-security-copy">
              Owners and tenants expect vetted pros on their property. Follow platform and local rules; carry the coverage your trade requires—this is guidance, not legal advice.
            </p>
          </div>
        </section>

        <section id="pro-faq" className="realtor-faq-section">
          <div className="section-header">
            <span className="section-badge">FAQ</span>
            <h2 className="section-title">Questions we hear often</h2>
            <p className="section-subtitle">Tap a row to expand. Still unsure? Use the form below—we read every request.</p>
          </div>
          <div className="realtor-faq-list">
            {FAQ_ITEMS.map((item, i) => (
              <div key={item.q} className={`realtor-faq-item ${faqOpen === i ? 'is-open' : ''}`}>
                <button
                  type="button"
                  className="realtor-faq-trigger"
                  aria-expanded={faqOpen === i}
                  onClick={() => setFaqOpen(faqOpen === i ? null : i)}
                >
                  <span>{item.q}</span>
                  <span className="realtor-faq-chevron" aria-hidden>{faqOpen === i ? '−' : '+'}</span>
                </button>
                {faqOpen === i && <div className="realtor-faq-answer">{item.a}</div>}
              </div>
            ))}
          </div>
        </section>

        <section id="pro-cta" className="realtor-final-cta">
          <div className="final-cta-content">
            <h2>Get started</h2>
            <p>Enter your business email — we will open account creation, then you finish onboarding (trades, qualifications, {PRO_SCREENING_DEPOSIT_DISPLAY} refundable deposit).</p>
            <div className="final-cta-email-row">
              <input
                type="email"
                placeholder="Business email"
                value={email}
                onChange={handleEmailChange}
                onKeyDown={(e) => e.key === 'Enter' && handleGetStarted()}
                className="realtor-final-email"
                aria-label="Business email"
              />
              <button
                type="button"
                onClick={handleGetStarted}
                className="realtor-cta-large"
              >
                Get started
                <span className="cta-arrow" aria-hidden>→</span>
              </button>
            </div>
            <textarea
              className="pro-landing-final-notes"
              placeholder="Optional: trade, service area, license type, years in business"
              value={notes}
              onChange={handleNotesChange}
              rows={3}
              maxLength={500}
              aria-label="Optional trade and business details"
            />
            {ctaError && (
              <p role="alert" className="realtor-access-feedback realtor-access-feedback--final realtor-access-feedback--error">
                {ctaError}
              </p>
            )}
            <p className="final-cta-note">hello@tenantdispatch.com • Replies from our team</p>
          </div>
        </section>
      </main>

      <div className={`realtor-sticky-cta ${showStickyCta ? 'is-visible' : ''}`} role="region" aria-label="Call to action">
        <div className="realtor-sticky-cta-inner">
          <span className="realtor-sticky-cta-label">Tenant Dispatch for Professionals</span>
          <button type="button" className="realtor-sticky-cta-btn" onClick={() => scrollToId('pro-cta')}>
            Get started
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfessionalLandingPage;
